import { Component, DestroyRef, ElementRef, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignDetailsComponent } from './campaign-details/campaign-details.component';
import { MessagesComponent } from './messages/messages.component';
import { RecipientsComponent } from './recipients/recipients.component';
import { ScheduleComponent } from './schedule/schedule.component';
import { ReviewComponent } from './review/review.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignStepperService } from '../../../../services/campaign/campaign-stepper.service';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { CampaignDetailsService } from '../../../../services/campaign/campaign-details.service';
import { RecipientsService } from '../../../../services/campaign/recipients.service';
import { MessagesService } from '../../../../services/campaign/messages.service';
import { ScheduleService } from '../../../../services/campaign/schedule.service';
import { CampaignsService } from '../../../../services/campaign/campaigns-api.service';
import { CancelCampaignService } from '../../../../services/campaign/cancel-campaign.service';
import { CampaignDuplicateService } from './campaign-duplicate.service';
import { WizardAudienceService } from './wizard-audience.service';

interface StepCopy {
  label: string;
  /** One line under the title saying what this step is for. */
  sub: string;
}

/** Sentence-case names and the one-line purpose of each step. */
const STEP_COPY: Record<string, StepCopy> = {
  'campaign-details': {
    label: 'Details',
    sub: 'Name the campaign and say what it should achieve.',
  },
  recipients: {
    label: 'Recipients',
    sub: 'Choose who receives it. People in more than one group are counted once.',
  },
  messages: {
    label: 'Messages',
    sub: 'Write the messages this campaign sends. You choose when each goes out next.',
  },
  schedule: {
    label: 'Schedule',
    sub: 'Set the dates the campaign runs, then when each message goes out.',
  },
  review: {
    label: 'Review',
    sub: 'Check the audience, copy and send times before it goes out.',
  },
};

@Component({
  selector: 'app-create-campaign',
  standalone: true,
  imports: [
    CampaignDetailsComponent,
    MessagesComponent,
    RecipientsComponent,
    ScheduleComponent,
    ReviewComponent,
    PromptCardComponent,
  ],
  templateUrl: './create-campaign.component.html',
  styleUrl: './create-campaign.component.css',
})
export class CreateCampaignComponent {
  campaignStepperService = inject(CampaignStepperService);
  campaignDetailsService = inject(CampaignDetailsService);
  recipientsService = inject(RecipientsService);
  messagesService = inject(MessagesService);
  scheduleService = inject(ScheduleService);
  campaignsService = inject(CampaignsService);
  duplicator = inject(CampaignDuplicateService);
  private audience = inject(WizardAudienceService);
  step?: string | null;
  steps = this.campaignStepperService.steps;
  nextStep = this.campaignStepperService.nextStep();
  cancelCampaignService = inject(CancelCampaignService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Visually hidden "Step n of 5" heading that takes focus when the step changes. */
  @ViewChild('stepTitle') private stepTitle?: ElementRef<HTMLElement>;

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((query) => {
      this.nextStep = this.campaignStepperService.nextStep();
      const requested = query.get('step') ?? 'campaign-details';
      const index = this.indexOf(requested);

      // Steps open in order: a link or refresh that lands past the first
      // unfinished step (the wizard keeps its data in memory) goes back to it.
      if (index === -1 || index > this.openUpTo) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { step: this.steps[this.openUpTo].slug, edit: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        return;
      }
      const changed = this.step !== undefined && this.step !== requested;
      this.step = requested;
      if (changed) this.revealStep();
    });
  }

  /** Index of the furthest step that may be opened: the first unfinished one. */
  get openUpTo(): number {
    const first = this.steps.findIndex((item) => !item.completed);
    return first === -1 ? this.steps.length - 1 : first;
  }

  get currentIndex(): number {
    return Math.max(0, this.indexOf(this.step ?? 'campaign-details'));
  }

  get copy(): StepCopy {
    return STEP_COPY[this.steps[this.currentIndex]?.slug] ?? STEP_COPY['campaign-details'];
  }

  labelOf(slug: string): string {
    return STEP_COPY[slug]?.label ?? slug;
  }

  stateOf(index: number): 'done' | 'current' | 'open' | 'locked' {
    if (index === this.currentIndex) return 'current';
    // A finished step past the first unfinished one cannot be opened yet.
    if (index > this.openUpTo) return 'locked';
    return this.steps[index]?.completed ? 'done' : 'open';
  }

  goTo(slug: string) {
    if (this.indexOf(slug) > this.openUpTo) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: slug, edit: null },
      queryParamsHandling: 'merge',
    });
  }

  cancelCampaignCreation() {
    this.campaignDetailsService.reset();
    this.recipientsService.reset();
    this.messagesService.resetSavedMessages();
    // The unsaved draft in the message form goes too: "lose everything" means everything.
    this.messagesService.reset();
    this.scheduleService.reset();
    this.campaignStepperService.reset();
    this.duplicator.clear();
    this.audience.reset();
    this.cancelCampaignService.closeCancelModal();
    this.router.navigate(['/organization/campaigns']);
  }

  /**
   * The button that changed the step is gone with the old step, so bring the
   * wizard's top back into view and move focus to the new step's heading,
   * which also tells screen readers which step is showing.
   */
  private revealStep() {
    if (!this.isBrowser) return;
    requestAnimationFrame(() => {
      const top = this.host.nativeElement;
      if (top.getBoundingClientRect().top < 0) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        top.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      }
      this.stepTitle?.nativeElement.focus({ preventScroll: true });
    });
  }

  private indexOf(slug: string): number {
    return this.steps.findIndex((item) => item.slug === slug);
  }
}
